import { CalendarDays, CalendarX2, Clock, Globe, MapPin, Tent } from 'lucide-react';
import type { EventDetail } from '../api/events';
import { eventColorVar } from '../brand/eventColors';
import {
  formatDayInZone,
  formatInZone,
  formatTimeInZone,
  isForeignZone,
} from '../lib/datetime';
import { FactRow } from '../ui/FactRow';
import { MapLink } from '../ui/MapLink';

/**
 * The canonical block from §5A: what the event *is*, as a record.
 *
 * Paper, squared corners, a serif title and a label/value table. Everything below the 3px rule is
 * talk; everything here is the claim. The rule is structural and is never softened to a 1px divider
 * (§10).
 *
 * **One component, both doors** — TROOP-116. The member's page and the parent's each drew this by
 * hand and had drifted: the member's said Depart/Return/Where with a type eyebrow, a foreign-zone
 * notice and the full cancellation block; the parent's said Starts/Ends/Leaves/Back/Where with none
 * of them, and put the description in secondary ink at 60ch. Nobody chose that. The facts are the
 * facts, and a parent reading about a campout from a work trip in Denver needs the zone notice at
 * least as much as a leader does.
 *
 * What still differs between the doors is what a person can *do*, which is a different question and
 * lives in the docket below — a guardian is not an officer, so `onEdit` is simply absent for them
 * rather than rendered disabled. A disabled control owes an explanation, and "you are not an
 * officer of this troop" is not something a parent can act on.
 *
 * Every time here renders through the event's zone rather than the viewer's.
 */
export function EventRecord({ event }: { event: EventDetail }) {
  const zone = event.timeZoneId;

  return (
    <div style={{ paddingTop: 'var(--space-6)' }}>
      <p
        className="t-eyebrow inline-flex items-center gap-2"
        style={{ color: 'var(--color-accent-announcement-text)', margin: 0 }}
      >
        <Tent size={14} aria-hidden="true" />
        {event.unit.name}
        {/* The kind sits on the eyebrow rather than in a fact row: it is what the event IS,
            not a fact about it, and the rows below are reserved for the answers a family
            came for. Absent entirely when the event is filed under nothing.
            In its own colour, and only here -- never on a fact value, never on a button,
            never behind the card. The unit's name keeps the ember it has always had. */}
        {event.eventTypeName && (
          <>
            {' · '}
            <span style={{ color: eventColorVar(event.eventTypeColorKey) }}>
              {event.eventTypeName}
            </span>
          </>
        )}
        {/* Cancelled has its own block below, so repeating it here would say the same
            thing twice in two registers. Any other non-published status still shows. */}
        {event.status !== 'Published' && event.status !== 'Cancelled' && ` · ${event.status}`}
      </p>

      <h1
        className="t-heading"
        style={{
          color: 'var(--color-text-primary)',
          margin: 'var(--space-2) 0 var(--space-1)',
          textWrap: 'balance',
        }}
      >
        {event.title}
      </h1>

      {/* The subtitle: what this particular one is about (TROOP-158). Under the title and
          above the dates, because it completes the name rather than qualifying the time --
          "Troop Meeting" then "Knots and lashings" then when it is.

          Absent, never a placeholder. Most events on a calendar have not been planned yet
          and a row with nothing under it is the right rendering of that; a greyed "No theme"
          would put a control-shaped nothing on every event in the unit. */}
      {event.theme && (
        <p
          className="t-body-strong"
          style={{
            color: 'var(--color-text-secondary)',
            margin: 'var(--space-1) 0 var(--space-2)',
            textWrap: 'balance',
          }}
        >
          {event.theme}
        </p>
      )}

      <p className="t-body tabular" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
        {formatRange(event.startsAt, event.endsAt, zone)}
      </p>

      {/* Above the facts, not below them. The facts are still true — this is still where
          the campout would have left from — but they are no longer a plan, and a family
          scanning for a departure time has to meet that first. The times stay legible
          rather than struck through: somebody rearranging their week needs to read what
          was called off, not squint at it. */}
      {event.status === 'Cancelled' && (
        <div
          role="status"
          style={{
            background: 'var(--color-status-blocked-surface)',
            border: '1px solid var(--color-status-blocked)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-3) var(--space-4)',
            marginTop: 'var(--space-4)',
          }}
        >
          <p
            className="t-body-strong inline-flex items-center gap-2"
            style={{ color: 'var(--color-text-primary)', margin: 0 }}
          >
            <CalendarX2 size={16} aria-hidden="true" />
            This event has been called off
          </p>
          {event.cancellationReason && (
            <p
              className="t-body"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 0' }}
            >
              {event.cancellationReason}
            </p>
          )}
          {event.cancelledAt && (
            <p
              className="t-caption tabular"
              style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-2) 0 0' }}
            >
              Called off {formatDayAndTime(event.cancelledAt, zone)}
            </p>
          )}
        </div>
      )}

      {event.description && (
        <p
          className="t-body"
          style={{ color: 'var(--color-text-primary)', marginTop: 'var(--space-5)' }}
        >
          {event.description}
        </p>
      )}

      {/* Only when it would otherwise mislead. A viewer in the unit's own zone is reading
          their own clock and does not need telling; a viewer somewhere else does, because
          every time below is the unit's and not theirs. */}
      {isForeignZone(zone) && (
        <p
          className="t-caption inline-flex items-center gap-2"
          style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--space-4)' }}
        >
          <Globe size={14} aria-hidden="true" />
          All times are {zone.replace(/_/g, ' ')} — where the {event.unit.unitType.toLowerCase()} is,
          not where you are.
        </p>
      )}

      {/* The fact table, and the head of the docket's spine. Everything from here down the left
          edge is one unbroken column of labels, and nothing may be inserted between this and the
          docket rows -- the seam being invisible is the whole of the idea. "Change these details"
          sat here once and cut the column in half. */}
      <div style={{ marginTop: 'var(--space-5)' }}>
        {/* The time, and no second line under it. Where the group leaves FROM belongs to a trip
            rather than to the event (TROOP-241): it is per-leg, it is what a driver reads, and as a
            column here it was the same fact twice with nothing reconciling the two. A trip carries
            it, with the map control on it — which is deliberately not a promise that a trip is on
            this screen: Travel renders nothing for an event with no trips that this reader cannot
            add one to, and no row at all on an event whose kind does not ask about travel. */}
        {event.departsAt ? (
          <FactRow label="Depart" icon={Clock}>
            {formatDayAndTime(event.departsAt, zone)}
          </FactRow>
        ) : (
          <FactRow label="Starts" icon={CalendarDays}>
            {formatDayAndTime(event.startsAt, zone)}
          </FactRow>
        )}

        {event.returnsAt ? (
          <FactRow label="Return" icon={Clock}>
            {formatDayAndTime(event.returnsAt, zone)}
          </FactRow>
        ) : (
          <FactRow label="Ends" icon={CalendarDays}>
            {formatDayAndTime(event.endsAt, zone)}
          </FactRow>
        )}

        {/* Either half is a place. The row used to be keyed on the name alone, so an event
            carrying only a street address rendered no Where row at all -- the address was typed,
            saved, and shown to nobody. The name is the value when there is one and the address
            the line beneath it; with no name the address is the value, because a row whose value
            is empty and whose caption is the answer reads as a fact that went missing.

            The control is the reader's own map app, never a map in here (TROOP-242). It is absent
            rather than disabled when there is nothing to search for, which is what the branch
            above already guarantees -- `MapLink` decides that itself and renders nothing.

            It sits under the place rather than in the row's trailing column, which is where it
            started. That column is `auto`-width and takes its share first, so on a phone a long
            place name was squeezed into a third of the row and broken over three lines while the
            control sat alone at the top right -- an affordance aligned with the column of chevrons
            and with nothing else. The name is the fact somebody is reading; it gets the width. */}
        {(event.locationName || event.locationAddress) && (
          <FactRow
            label="Where"
            icon={MapPin}
            tabular={false}
            secondary={
              <>
                {event.locationName && event.locationAddress}
                <div style={{ marginTop: 'var(--space-1)' }}>
                  <MapLink name={event.locationName} address={event.locationAddress} />
                </div>
              </>
            }
          >
            {event.locationName ?? event.locationAddress}
          </FactRow>
        )}
      </div>
    </div>
  );
}

/** "Fri 30 Oct, 5:00 PM" — day included, because a campout spans several. */
function formatDayAndTime(iso: string, zone: string): string {
  // Non-breaking space before the meridiem (spec §3).
  return formatInZone(iso, zone).replace(/ (AM|PM)/, ' $1');
}

/**
 * The date range under the title. Same day collapses to one date plus two times; a campout keeps
 * both days, which is the case that matters.
 */
function formatRange(startsAt: string, endsAt: string, zone: string): string {
  const startDay = formatDayInZone(startsAt, zone);
  const endDay = formatDayInZone(endsAt, zone);

  if (startDay === endDay) {
    return `${startDay}, ${formatTimeInZone(startsAt, zone)} – ${formatTimeInZone(endsAt, zone)}`;
  }

  return `${startDay} – ${endDay}`;
}
